import { useEffect, useState } from 'react'
import { useAction, useMutation } from 'convex/react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { ArrowRight, Loader2, Lock, X } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { api } from '../../../convex/_generated/api'
import { getStripe, lightAppearance } from '@/lib/stripe'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth-context'

// Hard paywall checkout (exp_paywall_pricing_v1 / hardpaywall99). Reuses the
// same inline Stripe Elements flow as the in-chat upgrade card: it asks the
// server for a checkout intent via createInlineCheckoutIntent({ planId: 'pro' })
// — the server resolves the effective paywall variant and, for a hardpaywall99
// user, returns a $99 no-trial subscription invoice PaymentIntent. The client
// just confirms it; the webhook flips planStatus → the modal closes and the
// caller proceeds.

const stripePromise = getStripe()

const paymentElementFields = { billingDetails: { address: 'auto' as const } }
const paymentElementTerms = {
  applePay: 'never' as const, auBecsDebit: 'never' as const, bancontact: 'never' as const,
  card: 'never' as const, cashapp: 'never' as const, googlePay: 'never' as const,
  ideal: 'never' as const, klarna: 'never' as const, paypal: 'never' as const,
  sepaDebit: 'never' as const, sofort: 'never' as const, usBankAccount: 'never' as const,
}

function HardPaywallForm({ clientSecret }: { clientSecret: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const markCheckoutPending = useMutation(api.stripe.markCheckoutPending)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        if (submitError.type !== 'card_error' && submitError.type !== 'validation_error') {
          setError(submitError.message ?? 'Please check your card details')
        }
        Sentry.captureException(new Error(submitError.message ?? 'Stripe elements.submit failed'), {
          tags: { source: 'hard_paywall', step: 'elements_submit', stripe_type: submitError.type },
        })
        setSubmitting(false)
        return
      }
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (confirmError) {
        if (confirmError.type !== 'card_error' && confirmError.type !== 'validation_error') {
          setError(confirmError.message ?? 'Payment failed')
        }
        Sentry.captureException(new Error(confirmError.message ?? 'Stripe confirmPayment failed'), {
          tags: { source: 'hard_paywall', step: 'confirmPayment', stripe_type: confirmError.type },
        })
        setSubmitting(false)
        return
      }
      // Success — keep spinner; the modal closes when planStatus flips via webhook.
      // Stamp the pending upgrade so the agent knows payment is processing during
      // the webhook lag (ISS-536). Fire-and-forget: failure just means status quo.
      markCheckoutPending({}).catch(() => {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
      Sentry.captureException(err, { tags: { source: 'hard_paywall', step: 'submit_exception' } })
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs', fields: paymentElementFields, terms: paymentElementTerms }} />
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-[12px] text-red-900 px-3 py-2">{error}</div>
      )}
      <div className="pt-1 space-y-2">
        <Button
          type="submit"
          disabled={submitting || !stripe || !elements}
          size="default"
          className="w-full font-semibold text-[16px] h-12 rounded-full bg-[#0385ff] text-white hover:bg-[#0270d8] shadow-[0_2px_8px_rgba(3,133,255,0.25)]"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</>
          ) : (
            <>Subscribe — $99/mo<ArrowRight className="h-4 w-4 ml-2" /></>
          )}
        </Button>
        <p className="text-center text-[13px] text-muted-foreground">
          Full access to Footage Pro. <span className="font-semibold text-foreground">Cancel anytime.</span>
        </p>
      </div>
    </form>
  )
}

export function HardPaywallModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const createIntent = useAction(api.stripe.createInlineCheckoutIntent)
  const { user } = useAuth()
  const isPaid = user?.planStatus === 'active' || user?.planStatus === 'trialing'
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [intentError, setIntentError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setIntentError(null)
      return
    }
    let cancelled = false
    // No trial — the server forces no-trial $99 for hardpaywall99 regardless,
    // but we ask for the non-trial intent explicitly.
    createIntent({ planId: 'pro' })
      .then(({ clientSecret }) => {
        if (!cancelled) setClientSecret(clientSecret)
      })
      .catch((err) => {
        if (cancelled) return
        setIntentError(err instanceof Error ? err.message : 'Could not start checkout')
        Sentry.captureException(err, { tags: { source: 'hard_paywall', step: 'createIntent' } })
      })
    return () => { cancelled = true }
  }, [open, createIntent])

  // Auto-close once the webhook flips planStatus to active/trialing.
  useEffect(() => {
    if (open && isPaid) onOpenChange(false)
  }, [open, isPaid, onOpenChange])

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true))
    else setVisible(false)
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className={`absolute inset-0 bg-neutral-200/60 backdrop-blur-[1px] transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`relative z-10 w-full max-w-[440px] rounded-2xl bg-card border border-neutral-300 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.14)] px-6 py-6 transition-all duration-150 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'}`}>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-20 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center justify-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-[#0385ff]" />
          <h2 className="text-center text-[20px] font-bold text-foreground">Subscribe to start</h2>
        </div>
        <p className="text-center text-[14px] text-muted-foreground mb-5">
          Footage Pro is <span className="font-semibold text-foreground">$99/mo</span>. Subscribe to create your first project.
        </p>

        {intentError && (
          <div className="rounded-md border border-red-200 bg-red-50 text-[12px] text-red-900 px-3 py-2 mb-3">{intentError}</div>
        )}

        {!clientSecret && !intentError && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: lightAppearance }}>
            <HardPaywallForm clientSecret={clientSecret} />
          </Elements>
        )}
      </div>
    </div>
  )
}
